import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Breadcrumb, Card, Checkbox, Col, Input, message, Progress, Row, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, FolderOpenOutlined, HomeOutlined, FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/common/security/AuthContext';
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';
import { dccList, dccMkdir, dccUpload, dccUploadTar } from '../../../services/dccApi';
import { buildTarBlob } from '../../../utils/tar';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

const UPLOAD_PAGE_ERROR_MAX_LEN = 280;

function formatUploadPageError(err: unknown, fallback: string): string {
  const raw =
    err instanceof Error && typeof err.message === 'string' && err.message.trim()
      ? err.message.trim()
      : typeof err === 'string' && err.trim()
        ? err.trim()
        : '';
  if (!raw) return fallback;
  return raw.length > UPLOAD_PAGE_ERROR_MAX_LEN ? `${raw.slice(0, UPLOAD_PAGE_ERROR_MAX_LEN)}…` : raw;
}

// ---------------------------------------------------------------------------
// FileSystem Entry API helpers (drag-drop klasör okuma)
// ---------------------------------------------------------------------------

type DragDataTransferItem = DataTransferItem & {
  webkitGetAsEntry?: () => any;
};

function attachRelativePath(file: File, relativePath: string): File {
  Object.defineProperty(file, 'webkitRelativePath', {
    configurable: true,
    value: relativePath,
  });
  return file;
}

function relativePathOf(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return typeof rel === 'string' ? rel.replace(/\\/g, '/').replace(/^\/+/, '') : '';
}

function topFolderOf(file: File): string | null {
  const rel = relativePathOf(file);
  const slash = rel.indexOf('/');
  if (slash <= 0) return null;
  return rel.slice(0, slash);
}

function parentPathOf(file: File): string {
  const rel = relativePathOf(file);
  const slash = rel.lastIndexOf('/');
  if (slash <= 0) return '';
  return rel.slice(0, slash);
}

function fileQueueKey(file: File): string {
  return `${relativePathOf(file) || file.name}\0${file.size}\0${file.lastModified}`;
}

function retargetTopFolder(file: File, nextTop: string): File {
  const rel = relativePathOf(file);
  const slash = rel.indexOf('/');
  const rest = slash >= 0 ? rel.slice(slash + 1) : file.name;
  const nextRel = `${nextTop}/${rest}`;
  if (nextRel === rel) return file;
  return attachRelativePath(file, nextRel);
}

function uniqueTopFolderName(desired: string, taken: Set<string>): string {
  if (!taken.has(desired)) return desired;
  let suffix = 2;
  while (taken.has(`${desired} (${suffix})`)) suffix += 1;
  return `${desired} (${suffix})`;
}

/** Aynı dosyayı atlar; aynı yoldaki değişmiş dosyayı günceller; adı çakışan farklı klasörü yeniden adlandırır. */
function mergeIntoQueue(prev: File[], incoming: File[]): { next: File[]; added: number; folderCount: number } {
  const takenTops = new Set<string>();
  const relsByTop = new Map<string, Set<string>>();
  prev.forEach((file) => {
    const top = topFolderOf(file);
    if (!top) return;
    takenTops.add(top);
    const rels = relsByTop.get(top) || new Set<string>();
    rels.add(relativePathOf(file));
    relsByTop.set(top, rels);
  });

  const groups = new Map<string, File[]>();
  const loose: File[] = [];
  incoming.forEach((file) => {
    const top = topFolderOf(file);
    if (!top) {
      loose.push(file);
      return;
    }
    const list = groups.get(top) || [];
    list.push(file);
    groups.set(top, list);
  });

  const prepared: File[] = [...loose];
  groups.forEach((files, top) => {
    const existingRels = relsByTop.get(top);
    const overlaps = !!existingRels && files.some((file) => existingRels.has(relativePathOf(file)));
    const targetTop = !overlaps && takenTops.has(top) ? uniqueTopFolderName(top, takenTops) : top;
    takenTops.add(targetTop);
    files.forEach((file) => prepared.push(retargetTopFolder(file, targetTop)));
  });

  const next = [...prev];
  const keyToIndex = new Map<string, number>();
  const relToIndex = new Map<string, number>();
  next.forEach((file, index) => {
    keyToIndex.set(fileQueueKey(file), index);
    relToIndex.set(relativePathOf(file) || file.name, index);
  });

  let added = 0;
  prepared.forEach((file) => {
    const key = fileQueueKey(file);
    if (keyToIndex.has(key)) return;
    const rel = relativePathOf(file) || file.name;
    const existingIndex = relToIndex.get(rel);
    if (existingIndex !== undefined) {
      next[existingIndex] = file;
      keyToIndex.set(key, existingIndex);
      added += 1;
      return;
    }
    keyToIndex.set(key, next.length);
    relToIndex.set(rel, next.length);
    next.push(file);
    added += 1;
  });

  const folderCount = new Set(prepared.map((file) => topFolderOf(file)).filter((name): name is string => !!name)).size;
  return { next: added ? next : prev, added, folderCount };
}

function readDroppedFile(entry: any): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDroppedEntries(reader: any): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const allEntries: any[] = [];
    const readBatch = () => {
      reader.readEntries(
        (entries: any[]) => {
          if (!entries.length) { resolve(allEntries); return; }
          allEntries.push(...entries);
          readBatch();
        },
        (error: unknown) => reject(error)
      );
    };
    readBatch();
  });
}

async function flattenDroppedEntry(entry: any, parentPath = ''): Promise<File[]> {
  const currentPath = [parentPath, entry?.name].filter(Boolean).join('/');
  if (entry?.isFile) {
    const file = await readDroppedFile(entry);
    return [attachRelativePath(file, currentPath)];
  }
  if (!entry?.isDirectory) return [];
  const childEntries = await readDroppedEntries(entry.createReader());
  const nestedFiles = await Promise.all(
    childEntries.map((child) => flattenDroppedEntry(child, currentPath))
  );
  return nestedFiles.flat();
}

// ---------------------------------------------------------------------------
// Tablo satırı
// ---------------------------------------------------------------------------

interface FolderRow {
  key: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Bileşen
// ---------------------------------------------------------------------------

const CoreFolderUploads: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [newFolder, setNewFolder] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploadTargetPath, setUploadTargetPath] = useState('');
  const [syncTargetWithBrowse, setSyncTargetWithBrowse] = useState(true);
  const [folderSearch, setFolderSearch] = useState('');
  const [uploadMode, setUploadMode] = useState<'files' | 'folders'>('files');
  const [dragOver, setDragOver] = useState(false);
  const [readingDrop, setReadingDrop] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const pendingRef = useRef<File[]>([]);

  // Ref: loadFolders içindeki closure sync sorununu çözer.
  // syncTargetWithBrowse dep'e girince her toggle'da useEffect yeniden tetiklenip
  // navigasyon root'a sıfırlanıyordu. Ref ile bunu önleriz.
  const syncTargetWithBrowseRef = useRef(syncTargetWithBrowse);
  useEffect(() => {
    syncTargetWithBrowseRef.current = syncTargetWithBrowse;
  }, [syncTargetWithBrowse]);

  // File input ref'leri (webkitdirectory için)
  const folderInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Klasör listesi
  // ---------------------------------------------------------------------------

  const loadFolders = useCallback(
    async (path = '') => {
      if (!token) return;
      setLoading(true);
      try {
        const data = await dccList(path, token);
        if (Array.isArray(data)) {
          const mapped: FolderRow[] = data
            .filter((item: string) => {
              const raw = item.replace(/\/$/, '');
              return item.endsWith('/') || !/\.[^./\\]+$/.test(raw);
            })
            .map((item: string) => ({ key: item, name: item.replace(/\/$/, '') }));
          setFolders(mapped);
          setCurrentPath(path);
          // syncTargetWithBrowseRef kullanılıyor — dep'e eklemeye gerek yok
          setUploadTargetPath((prev) => (syncTargetWithBrowseRef.current ? path : prev));
        } else {
          setFolders([]);
          message.error(t('coreFolderUploads.messages.listFailed'));
        }
      } catch (e) {
        setFolders([]);
        message.error(formatUploadPageError(e, t('coreFolderUploads.messages.serverUnreachable')));
      } finally {
        setLoading(false);
      }
    },
    [token, t] // syncTargetWithBrowse KASITLI OLARAK DEP LİSTESİNDEN ÇIKARILDI
  );

  useEffect(() => {
    if (!token) {
      setFolders([]);
      setCurrentPath('');
      setUploadTargetPath('');
      setSyncTargetWithBrowse(true);
      pendingRef.current = [];
      setPendingFiles([]);
      return;
    }
    loadFolders('');
  }, [token, loadFolders]);

  const handleEnterFolder = (name: string) => {
    const nextPath = currentPath ? `${currentPath}/${name}` : name;
    loadFolders(nextPath);
  };

  const handleGoUp = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    loadFolders(parts.join('/'));
  };

  const handleRefreshFolders = () => loadFolders(currentPath);

  const applyCurrentFolderAsUploadTarget = () => {
    setUploadTargetPath(currentPath);
    setSyncTargetWithBrowse(false);
    message.success(t('coreFolderUploads.targetSetSuccess'));
  };

  // ---------------------------------------------------------------------------
  // Yükleme — TAR streaming (klasör yapısını korur, RAM dostu)
  // ---------------------------------------------------------------------------

  const uploadDirectPreservingFolders = useCallback(
    async (files: File[]): Promise<{ uploadedCount: number; failed: Array<{ file: string; error: string }> }> => {
      // Upload without TAR: group by parent folder and call dccUpload once per folder.
      // This avoids `file.arrayBuffer()` failures (some files can be streamed but not read into JS memory).
      const groups = new Map<string, File[]>();
      files.forEach((file) => {
        const parent = parentPathOf(file); // '' for loose files
        const list = groups.get(parent) || [];
        list.push(file);
        groups.set(parent, list);
      });

      const entries = Array.from(groups.entries());
      let uploadedCount = 0;
      const failed: Array<{ file: string; error: string }> = [];

      for (let i = 0; i < entries.length; i++) {
        const [parent, groupFiles] = entries[i];
        const path = [uploadTargetPath || '', parent].filter(Boolean).join('/');

        const tryBatch = async () => {
          await dccUpload(path, groupFiles, token);
          uploadedCount += groupFiles.length;
        };

        try {
          await tryBatch();
        } catch (e1) {
          // Some backends require the path to exist. Create and retry once.
          try {
            if (path) {
              await dccMkdir(path, token);
              await tryBatch();
            } else {
              throw e1;
            }
          } catch (e2) {
            // If a batch fails, retry file-by-file to isolate the problematic file(s).
            for (const file of groupFiles) {
              try {
                await dccUpload(path, [file], token);
                uploadedCount += 1;
              } catch (e3) {
                failed.push({
                  file: relativePathOf(file) || file.name,
                  error: formatUploadPageError(e3, 'Upload failed'),
                });
              }
            }
          }
        }
        setZipProgress(Math.round(((i + 1) / entries.length) * 95));
      }

      return { uploadedCount, failed };
    },
    [token, uploadTargetPath]
  );

  const zipAndUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) {
        message.error(t('coreFolderUploads.messages.noFiles'));
        return;
      }
      if (!token) {
        message.warning(t('coreFolderUploads.messages.loginRequired'));
        return;
      }

      setUploading(true);
      setZipProgress(0);
      const hide = message.loading(t('coreFolderUploads.messages.uploading'), 0);

      try {
        let tarBlob: Blob | null = null;
        try {
          // TAR arşivi oluştur (webkitRelativePath ile klasör yapısı korunur)
          tarBlob = await buildTarBlob(files, { onProgress: setZipProgress });
        } catch (e) {
          // Some files fail at arrayBuffer() / in-memory packing; fall back to direct streaming upload.
          message.warning(t('coreFolderUploads.messages.tarFallback'));
          const direct = await uploadDirectPreservingFolders(files);
          setZipProgress(100);
          hide();
          if (direct.failed.length) {
            message.warning(t('coreFolderUploads.messages.directPartial', { failed: direct.failed.length }));
            const failedSet = new Set(direct.failed.map((f) => f.file));
            const keep = files.filter((f) => failedSet.has(relativePathOf(f) || f.name));
            pendingRef.current = keep;
            setPendingFiles(keep);
          } else {
            pendingRef.current = [];
            setPendingFiles([]);
          }
          await loadFolders(currentPath);
          return;
        }

        setZipProgress(95);

        let data: any;
        try {
          data = await dccUploadTar(uploadTargetPath || '', tarBlob, token);
        } catch (e) {
          // TAR parsing can fail server-side for some edge cases; fall back to direct upload.
          message.warning(t('coreFolderUploads.messages.tarFallback'));
          const direct = await uploadDirectPreservingFolders(files);
          setZipProgress(100);
          hide();
          if (direct.failed.length) {
            message.warning(t('coreFolderUploads.messages.directPartial', { failed: direct.failed.length }));
            const failedSet = new Set(direct.failed.map((f) => f.file));
            const keep = files.filter((f) => failedSet.has(relativePathOf(f) || f.name));
            pendingRef.current = keep;
            setPendingFiles(keep);
          } else {
            pendingRef.current = [];
            setPendingFiles([]);
          }
          await loadFolders(currentPath);
          return;
        }

        setZipProgress(100);
        hide();

        const uploadedCount = Array.isArray((data as any)?.uploaded)
          ? (data as any).uploaded.length
          : files.length;
        message.success(
          t('coreFolderUploads.messages.uploadedCount', {
            count: uploadedCount,
            path: uploadTargetPath || '',
          })
        );

        const tarErrors = Array.isArray((data as any)?.errors) ? (data as any).errors : [];
        if (tarErrors.length) {
          message.warning(t('coreFolderUploads.messages.tarPartial', { failed: tarErrors.length }));
          const failedSet = new Set(
            tarErrors
              .map((e: any) => String(e?.file ?? '').trim())
              .filter(Boolean)
          );
          const keep = files.filter((f) => failedSet.has(relativePathOf(f) || f.name));
          pendingRef.current = keep;
          setPendingFiles(keep);
        } else {
          pendingRef.current = [];
          setPendingFiles([]);
        }
        await loadFolders(currentPath);
      } catch (e) {
        hide();
        message.error(formatUploadPageError(e, t('coreFolderUploads.messages.uploadFailedNetwork')));
      } finally {
        setUploading(false);
        window.setTimeout(() => setZipProgress(0), 1500);
      }
    },
    [currentPath, loadFolders, t, token, uploadTargetPath, uploadDirectPreservingFolders]
  );

  const replaceQueue = useCallback((files: File[]) => {
    pendingRef.current = files;
    setPendingFiles(files);
  }, []);

  const enqueueFiles = useCallback(
    (incoming: File[]) => {
      if (!incoming.length || uploading) return;
      const { next, added, folderCount } = mergeIntoQueue(pendingRef.current, incoming);
      if (!added) {
        message.info(t('coreFolderUploads.messages.queueNoNewFiles'));
        return;
      }
      replaceQueue(next);
      message.success(t('coreFolderUploads.messages.queueAdded', { count: added, folderCount }));
    },
    [replaceQueue, t, uploading]
  );

  const clearQueue = useCallback(() => {
    replaceQueue([]);
  }, [replaceQueue]);

  const removeQueuedGroup = useCallback(
    (topFolder: string | null) => {
      replaceQueue(pendingRef.current.filter((file) => topFolderOf(file) !== topFolder));
    },
    [replaceQueue]
  );

  const handleUploadQueued = useCallback(() => {
    if (!pendingRef.current.length) {
      message.warning(t('coreFolderUploads.messages.noQueuedFiles'));
      return;
    }
    void zipAndUpload(pendingRef.current);
  }, [t, zipAndUpload]);

  // ---------------------------------------------------------------------------
  // Drag-drop (dosya ve klasör)
  // ---------------------------------------------------------------------------

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  // relatedTarget kontrolü ile iç elemanlara geçişte titreme engellenir
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      if (uploading || readingDrop) return;

      if (uploadMode === 'folders') {
        const items = Array.from(e.dataTransfer?.items || []) as DragDataTransferItem[];
        const entries = items.map((item) => item.webkitGetAsEntry?.()).filter(Boolean);

        if (!entries.length) return;

        const hasDirectory = (entries as any[]).some((entry) => entry?.isDirectory);

        try {
          if (hasDirectory) {
            setReadingDrop(true);
            const nested = await Promise.all(entries.map((entry: any) => flattenDroppedEntry(entry)));
            enqueueFiles(nested.flat());
          } else {
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length) enqueueFiles(files);
          }
        } catch (err) {
          message.error(formatUploadPageError(err, t('coreFolderUploads.messages.dropFolderReadFailed')));
        } finally {
          setReadingDrop(false);
        }
      } else {
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length) enqueueFiles(files);
      }
    },
    [enqueueFiles, readingDrop, t, uploadMode, uploading]
  );

  // ---------------------------------------------------------------------------
  // Input onChange (webkitdirectory / multiple)
  // ---------------------------------------------------------------------------

  const handleFolderInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (files.length) enqueueFiles(files);
    },
    [enqueueFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (files.length) enqueueFiles(files);
    },
    [enqueueFiles]
  );

  const handleDropZoneClick = useCallback(() => {
    if (uploading || readingDrop) return;
    if (uploadMode === 'folders') folderInputRef.current?.click();
    else fileInputRef.current?.click();
  }, [readingDrop, uploadMode, uploading]);

  // ---------------------------------------------------------------------------
  // Klasör oluştur
  // ---------------------------------------------------------------------------

  const handleCreateFolder = async () => {
    const name = newFolder.trim();
    if (!name) {
      message.warning(t('coreFolderUploads.messages.enterFolderName'));
      return;
    }
    setCreatingFolder(true);
    try {
      const path = currentPath ? `${currentPath}/${name}` : name;
      await dccMkdir(path, token);
      message.success(t('coreFolderUploads.messages.folderCreated'));
      setNewFolder('');
      await loadFolders(currentPath);
    } catch (e) {
      message.error(formatUploadPageError(e, t('coreFolderUploads.messages.folderCreateFailed')));
    } finally {
      setCreatingFolder(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Türetilmiş değerler
  // ---------------------------------------------------------------------------

  const queueSummary = useMemo(() => {
    const folderCounts = new Map<string, number>();
    let looseCount = 0;
    pendingFiles.forEach((file) => {
      const top = topFolderOf(file);
      if (!top) {
        looseCount += 1;
        return;
      }
      folderCounts.set(top, (folderCounts.get(top) || 0) + 1);
    });
    return {
      fileCount: pendingFiles.length,
      folderCount: folderCounts.size,
      looseCount,
      folders: Array.from(folderCounts.entries()).map(([name, count]) => ({ name, count })),
    };
  }, [pendingFiles]);

  const displayedFolders = useMemo(() => {
    const q = folderSearch.trim().toLocaleLowerCase('tr');
    if (!q) return folders;
    return folders.filter((row) => row.name.toLocaleLowerCase('tr').includes(q));
  }, [folders, folderSearch]);

  const displayBrowse = `/root/uploads/${currentPath || ''}`.replace(/\/$/, '') || '/root/uploads';
  const displayTarget = `/root/uploads/${uploadTargetPath || ''}`.replace(/\/$/, '') || '/root/uploads';
  const targetMatchesBrowse = uploadTargetPath === currentPath;

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <span style={{ cursor: 'pointer' }} onClick={() => loadFolders('')}>
            <HomeOutlined /> {t('coreFolderUploads.breadcrumbRoot')}
          </span>
        ),
      },
      ...currentPath
        .split('/')
        .filter(Boolean)
        .map((segment, idx, arr) => ({
          title: (
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => loadFolders(arr.slice(0, idx + 1).join('/'))}
            >
              {segment}
            </span>
          ),
        })),
    ],
    [currentPath, loadFolders, t]
  );

  const columns = [
    {
      title: t('coreFolderUploads.columnFolder'),
      dataIndex: 'name',
      key: 'name',
      render: (_text: string, record: FolderRow) => (
        <span
          style={{ cursor: 'pointer', color: '#1677ff' }}
          onClick={() => handleEnterFolder(record.name)}
        >
          <FolderOpenOutlined /> {record.name}
        </span>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <ProtectedRoute>
      <PageGuard>
        <div style={{ padding: 24 }}>
          {/* Gizli input'lar */}
          <input
            ref={folderInputRef}
            type="file"
            // @ts-ignore — webkitdirectory TS tip tanımında yok
            webkitdirectory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleFolderInputChange}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                <Title level={2} style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'normal' }}>
                  {t('coreFolderUploads.pageTitle')}
                </Title>
                <Text type="secondary">{t('coreFolderUploads.pageSubtitle')}</Text>
              </div>
            </div>

            <Breadcrumb items={breadcrumbItems} />

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space wrap align="center">
                <Checkbox
                  checked={syncTargetWithBrowse}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setSyncTargetWithBrowse(on);
                    if (on) setUploadTargetPath(currentPath);
                  }}
                >
                  {t('coreFolderUploads.syncCheckbox')}
                </Checkbox>
                <Button
                  size="small"
                  type={!targetMatchesBrowse ? 'primary' : 'default'}
                  disabled={syncTargetWithBrowse || targetMatchesBrowse}
                  onClick={applyCurrentFolderAsUploadTarget}
                >
                  {t('coreFolderUploads.setBrowseAsTarget')}
                </Button>
              </Space>
              {!targetMatchesBrowse && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('coreFolderUploads.alertTitleMismatch')}
                  description={
                    <>
                      <div>
                        <Text type="secondary">{t('coreFolderUploads.labelDisplayed')} </Text>
                        <code>{displayBrowse}</code>
                      </div>
                      <div>
                        <Text type="secondary">{t('coreFolderUploads.labelFilesGoTo')} </Text>
                        <code>{displayTarget}</code>
                      </div>
                    </>
                  }
                />
              )}
            </Space>

            <Row gutter={16}>
              {/* Klasör gezgini */}
              <Col xs={24} md={14}>
                <Card
                  title={t('coreFolderUploads.listTitle')}
                  extra={
                    <Space wrap size="small" style={{ justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={handleGoUp} disabled={!currentPath}>
                        {t('coreFolderUploads.goUp')}
                      </Button>
                      <Input.Search
                        allowClear
                        placeholder={t('coreFolderUploads.searchPlaceholder')}
                        value={folderSearch}
                        onChange={(e) => setFolderSearch(e.target.value)}
                        style={{ width: 220, maxWidth: 'min(220px, 55vw)' }}
                      />
                      <Button size="small" onClick={handleRefreshFolders} loading={loading}>
                        {t('coreFolderUploads.refresh')}
                      </Button>
                    </Space>
                  }
                >
                  <Space wrap size="small" style={{ marginBottom: 12 }}>
                    <Input
                      placeholder={t('coreFolderUploads.newFolderPlaceholder')}
                      size="small"
                      style={{ width: 220, maxWidth: '100%' }}
                      value={newFolder}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolder(e.target.value)}
                      onPressEnter={handleCreateFolder}
                      disabled={creatingFolder}
                    />
                    <Button action="add"
                      type="primary"
                      size="small"
                      onClick={handleCreateFolder}
                      loading={creatingFolder}
                    >
                      {t('coreFolderUploads.createFolder')}
                    </Button>
                  </Space>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    {t('coreFolderUploads.browseHint', {
                      syncSuffix: syncTargetWithBrowse
                        ? t('coreFolderUploads.browseHintSyncOn')
                        : t('coreFolderUploads.browseHintSyncOff'),
                    })}
                  </Text>
                  <Spin spinning={loading} tip={t('coreFolderUploads.loadingFoldersTip')}>
                    <Table
                      columns={columns}
                      dataSource={displayedFolders}
                      rowKey="key"
                      pagination={false}
                      size="middle"
                      locale={{
                        emptyText:
                          folders.length === 0
                            ? t('coreFolderUploads.emptyFolders')
                            : t('coreFolderUploads.emptyFiltered'),
                      }}
                    />
                  </Spin>
                </Card>
              </Col>

              {/* Upload kartı */}
              <Col xs={24} md={10}>
                <Card
                  title={t('coreFolderUploads.uploadCardTitle')}
                  extra={
                    <Segmented
                      size="small"
                      value={uploadMode}
                      onChange={(value) => setUploadMode(value as 'files' | 'folders')}
                      options={[
                        { label: t('coreFolderUploads.uploadModeFiles'), value: 'files' },
                        { label: t('coreFolderUploads.uploadModeFolders'), value: 'folders' },
                      ]}
                      disabled={uploading || readingDrop || !token}
                    />
                  }
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* Hedef klasör göstergesi */}
                    <div
                      style={{
                        padding: '12px 14px',
                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                        borderRadius: 8,
                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                      }}
                    >
                      <Space align="center" wrap size="small" style={{ marginBottom: 6 }}>
                        <Text strong>{t('coreFolderUploads.uploadTargetLabel')}</Text>
                        <Tag color="blue">{t('coreFolderUploads.tagServer')}</Tag>
                      </Space>
                      <div>
                        <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{displayTarget}</code>
                      </div>
                      {uploadTargetPath === '' ? (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                          {t('coreFolderUploads.hintRootTarget')}
                        </Text>
                      ) : (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                          {t('coreFolderUploads.hintSubPathPrefix')}{' '}
                          <code>{uploadTargetPath}</code>
                        </Text>
                      )}
                    </div>

                    {/* Drop zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleDropZoneClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDropZoneClick(); }}
                      style={{
                        border: `2px dashed ${dragOver ? '#1677ff' : uploadMode === 'folders' ? '#91caff' : '#d9d9d9'}`,
                        borderRadius: 8,
                        padding: '32px 20px',
                        textAlign: 'center',
                        cursor: uploading || readingDrop || !token ? 'not-allowed' : 'pointer',
                        background: dragOver
                          ? 'rgba(22, 119, 255, 0.06)'
                          : uploadMode === 'folders'
                            ? 'rgba(22, 119, 255, 0.02)'
                            : 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.01))',
                        transition: 'border-color 0.2s, background 0.2s',
                        opacity: uploading || readingDrop || !token ? 0.5 : 1,
                      }}
                    >
                      {uploadMode === 'folders' ? (
                        <FolderOpenOutlined
                          style={{ fontSize: 40, color: dragOver ? '#1677ff' : '#69b1ff', marginBottom: 8 }}
                        />
                      ) : (
                        <FileAddOutlined
                          style={{ fontSize: 40, color: dragOver ? '#1677ff' : '#bfbfbf', marginBottom: 8 }}
                        />
                      )}
                      <p style={{ marginBottom: 4, fontWeight: 500 }}>
                        {uploadMode === 'folders'
                          ? t('coreFolderUploads.uploadClickOrDragFolders')
                          : t('coreFolderUploads.uploadClickOrDragFiles')}
                      </p>
                      <p style={{ margin: 0, color: '#8c8c8c', fontSize: 12 }}>
                        {readingDrop
                          ? t('coreFolderUploads.readingDropped')
                          : uploadMode === 'folders'
                            ? t('coreFolderUploads.uploadHintFolders')
                            : t('coreFolderUploads.uploadHintFiles')}
                      </p>
                      {uploadMode === 'folders' && !readingDrop && (
                        <p style={{ margin: '8px 0 0', color: '#8c8c8c', fontSize: 12 }}>
                          {t('coreFolderUploads.folderMultiHint')}
                        </p>
                      )}
                      {dragOver && (
                        <p style={{ margin: '8px 0 0', color: '#1677ff', fontSize: 12, fontWeight: 500 }}>
                          {uploadMode === 'folders'
                            ? t('coreFolderUploads.dropReleaseFolders')
                            : t('coreFolderUploads.dropReleaseFiles')}
                        </p>
                      )}
                    </div>

                    <div>
                      {queueSummary.fileCount === 0 ? (
                        <Text type="secondary">{t('coreFolderUploads.pendingEmptyHint')}</Text>
                      ) : (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Space wrap size={4}>
                            <Tag>{t('coreFolderUploads.pendingFilesTag', { count: queueSummary.fileCount })}</Tag>
                            {queueSummary.folderCount > 0 && (
                              <Tag color="blue">
                                {t('coreFolderUploads.pendingFoldersTag', { count: queueSummary.folderCount })}
                              </Tag>
                            )}
                            {queueSummary.looseCount > 0 && (
                              <Tag>{t('coreFolderUploads.pendingLooseFilesTag', { count: queueSummary.looseCount })}</Tag>
                            )}
                          </Space>
                          <Text type="secondary">{t('coreFolderUploads.pendingReadyHint')}</Text>
                          <div style={{ maxHeight: 180, overflow: 'auto' }}>
                            {queueSummary.folders.map((folder) => (
                              <div
                                key={folder.name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  padding: '4px 0',
                                }}
                              >
                                <Text ellipsis style={{ flex: 1 }}>
                                  <FolderOpenOutlined /> {folder.name}{' '}
                                  <Text type="secondary">
                                    ({t('coreFolderUploads.queuedItemCount', { count: folder.count })})
                                  </Text>
                                </Text>
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  aria-label={t('coreFolderUploads.removeQueued')}
                                  disabled={uploading}
                                  onClick={() => removeQueuedGroup(folder.name)}
                                />
                              </div>
                            ))}
                            {queueSummary.looseCount > 0 && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  padding: '4px 0',
                                }}
                              >
                                <Text ellipsis style={{ flex: 1 }}>
                                  <FileAddOutlined />{' '}
                                  {t('coreFolderUploads.pendingLooseFilesTag', { count: queueSummary.looseCount })}
                                </Text>
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  aria-label={t('coreFolderUploads.removeQueued')}
                                  disabled={uploading}
                                  onClick={() => removeQueuedGroup(null)}
                                />
                              </div>
                            )}
                          </div>
                        </Space>
                      )}
                    </div>

                    <Space wrap>
                      <Button action="add"
                        type="primary"
                        onClick={handleUploadQueued}
                        loading={uploading}
                        disabled={!token || readingDrop || queueSummary.fileCount === 0}
                      >
                        {t('coreFolderUploads.uploadSelected')}
                      </Button>
                      <Button onClick={clearQueue} disabled={uploading || queueSummary.fileCount === 0}>
                        {t('coreFolderUploads.clearSelection')}
                      </Button>
                    </Space>

                    {/* İlerleme çubuğu (zipleme + yükleme) */}
                    {zipProgress > 0 && (
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {zipProgress < 90
                            ? t('coreFolderUploads.progressReading')
                            : zipProgress < 95
                              ? t('coreFolderUploads.progressPacking')
                              : t('coreFolderUploads.progressUploading')}
                        </Text>
                        <Progress
                          percent={zipProgress}
                          status={zipProgress < 100 ? 'active' : 'success'}
                          size="small"
                        />
                      </Space>
                    )}
                  </Space>
                </Card>
              </Col>
            </Row>
          </Space>
        </div>
      </PageGuard>
    </ProtectedRoute>
  );
};

export default CoreFolderUploads;
